import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CalcsFilingGroupStatusService } from './calcs-filing-group-status.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gtw-calcs-filing-group-status',
    templateUrl: './calcs-filing-group-status.component.html',
    styleUrls: ['./calcs-filing-group-status.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CalcsFilingGroupStatusComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private static readonly bridgeHostSelector = 'gtw-calcs-filing-group-status-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subscriptions = new Subscription();
  private fetchWave = 0;
  private saveWave = 0;
  private copyInFlight = false;

  baseURLs: any = {};
  globalParams: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};
  
  showJsonLoader: boolean = false;
  taxYearsList: any[] = [];
  tax_year: any = {};
  FromScenarioList: any[] = [];
  ToScenarioList: any[] = [];
  FromFilingGroupList: any[] = [];
  ToFilingGroupList: any[] = [];

  fromTaxYearFlag: boolean = false;
  toTaxYearFlag: boolean = false;

  selectedFromTaxYear: any = null;
  selectedToTaxYear: any = null;
  selectedFromScenario: any = null;
  selectedToScenario: any = null;
  selectedFromFilingGroup: any = null;
  selectedToFilingGroup: any = null;

  clientKey: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private calcsFilingGroupStatusService: CalcsFilingGroupStatusService,
    private gtwGlobal: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.workspaceService.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private nextFetchWave(): number {
    return ++this.fetchWave;
  }

  private nextSaveWave(): number {
    return ++this.saveWave;
  }

  private emitRequestGridUpdate(detail: 'refresh' | 'cancel'): void {
    this.requestGridUpdate.emit(detail);
    const host = document.querySelector(CalcsFilingGroupStatusComponent.bridgeHostSelector);
    host?.dispatchEvent(new CustomEvent('request-grid-update', { detail, bubbles: true }));
  }

  private resolveApiBase(): string {
    const gc = this.GENERAL_CONFIG || {};
    return String(gc.base_url || gc.api || '').trim();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = this.baseURLs;

    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.gtwGlobal.global?.globalParams || {});
    }
    this.filterParams = fp;

    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      this.rowData = {};
    } else {
      const rd = extractObject(rawRow);
      this.rowData = Object.keys(rd).length > 0 ? rd : {};
    }

    this.USER_SETTINGS = this.appService.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG || {};

    const appTaxYears = this.appService.TAX_YEARS;
    this.tax_year = (appTaxYears && typeof appTaxYears === 'object') ? appTaxYears : {};

    this.globalParams = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
  }

  private tryInit(): void {
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      return;
    }
    if (!this.globalParams || Object.keys(this.globalParams).length === 0) {
      return;
    }
    this.init();
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.globalParams && Object.keys(this.globalParams).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    const wave = this.nextFetchWave();

    if (this.USER_SETTINGS?.user?.client_key) {
      this.clientKey = this.USER_SETTINGS.user.client_key;
    }

    this.initializeTaxYears(wave);
  }

  initializeTaxYears(wave: number): void {
    this.fetchTaxYears(wave);
  }

  fetchTaxYears(wave: number): void {
    if (this.tax_year && Object.keys(this.tax_year).length > 0) {
      this.taxYearsList = this.normalizeTaxYears(this.tax_year);

      if (!this.taxYearsList.length) {
        this.createDefaultTaxYearsList(wave);
        return;
      }

      const currentTaxYear = String(this.globalParams?.tax_year ?? '');
      const selectedTaxYear =
        this.taxYearsList.find((item: any) =>
          String(item?.label ?? '') === currentTaxYear || String(item?.value ?? '') === currentTaxYear
        ) || this.taxYearsList[0];

      this.selectedFromTaxYear = selectedTaxYear;
      this.selectedToTaxYear = selectedTaxYear;

      if (this.selectedFromTaxYear) {
        this.fetchFromScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
        this.fetchToScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
        this.fetchFromFilingGroup(this.selectedToTaxYear.value, this.selectedToTaxYear.scenario, wave);
        this.fetchToFilingGroup(this.selectedToTaxYear.value, this.selectedToTaxYear.scenario, wave);
      }
    } else {
      this.createDefaultTaxYearsList(wave);
    }
  }

  private normalizeTaxYears(source: any): any[] {
    const normalized: any[] = [];
    const defaultScenario = String(this.globalParams?.scenario ?? '74');

    const pushNormalized = (entry: any, keyHint?: string): void => {
      if (entry === null || entry === undefined) {
        return;
      }

      if (Array.isArray(entry)) {
        entry.forEach((inner: any) => pushNormalized(inner, keyHint));
        return;
      }

      if (typeof entry === 'object') {
        const label = entry.label ?? entry.value ?? keyHint;
        const value = entry.value ?? entry.label ?? keyHint;
        if (label === null || label === undefined || label === '') {
          return;
        }
        normalized.push({
          ...entry,
          label: String(label),
          value: String(value),
          scenario: String(entry.scenario ?? defaultScenario)
        });
        return;
      }

      if (keyHint !== undefined) {
        normalized.push({
          label: String(keyHint),
          value: String(entry || keyHint),
          scenario: defaultScenario
        });
      }
    };

    if (Array.isArray(source)) {
      source.forEach((item: any) => pushNormalized(item));
    } else {
      _.forEach(source, (value: any, key: string) => pushNormalized(value, key));
    }

    return _.uniqBy(normalized, (item: any) => `${item.value}|${item.scenario}`);
  }

  createDefaultTaxYearsList(wave: number): void {
    const currentYear = new Date().getFullYear();
    const taxYears: any[] = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      taxYears.push({
        label: i.toString(),
        value: i.toString(),
        scenario: this.globalParams?.scenario || '74'
      });
    }
    this.taxYearsList = taxYears;

    const currentTaxYear = String(this.globalParams?.tax_year ?? '');
    const selectedTaxYear =
      this.taxYearsList.find((item: any) =>
        String(item?.label ?? '') === currentTaxYear || String(item?.value ?? '') === currentTaxYear
      ) || this.taxYearsList[0];

    if (selectedTaxYear) {
      this.selectedFromTaxYear = selectedTaxYear;
      this.selectedToTaxYear = selectedTaxYear;
      this.fetchFromScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
      this.fetchToScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
      this.fetchFromFilingGroup(this.selectedToTaxYear.value, this.selectedToTaxYear.scenario, wave);
      this.fetchToFilingGroup(this.selectedToTaxYear.value, this.selectedToTaxYear.scenario, wave);
    }
  }

  private jcdKeyForRequest(): number {
    const j = this.globalParams?.jcd_key;
    if (j === undefined || j === null || j === '') {
      return 250;
    }
    const n = parseInt(String(j), 10);
    return Number.isFinite(n) ? n : 250;
  }

  fetchFromScenario(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.jcdKeyForRequest()
    };
    
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.showJsonLoader = false;
      return;
    }

    this.calcsFilingGroupStatusService.getFromScenario(apiBase, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        this.FromScenarioList = (data.data && data.data.jsonObject) ? data.data.jsonObject : (data.jsonObject || []);
        const selectedScenarioIndex = _.findIndex(this.FromScenarioList, { 'VALUE': parseInt(scenario, 10) });
        if (selectedScenarioIndex >= 0) {
          this.selectedFromScenario = this.FromScenarioList[selectedScenarioIndex];
        }
        this.showJsonLoader = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching from scenario:', error);
        this.showJsonLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading scenario data', 'gtw-snackbar--red');
      }
    );
  }

  fetchToScenario(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.jcdKeyForRequest()
    };
    
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.showJsonLoader = false;
      return;
    }

    this.calcsFilingGroupStatusService.getFromScenario(apiBase, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        this.ToScenarioList = (data.data && data.data.jsonObject) ? data.data.jsonObject : (data.jsonObject || []);
        const selectedScenarioIndex = _.findIndex(this.ToScenarioList, { 'VALUE': parseInt(scenario, 10) });
        if (selectedScenarioIndex >= 0) {
          this.selectedToScenario = this.ToScenarioList[selectedScenarioIndex];
        }
        this.showJsonLoader = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching to scenario:', error);
        this.showJsonLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading scenario data', 'gtw-snackbar--red');
      }
    );
  }

  fetchFromFilingGroup(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.jcdKeyForRequest()
    };
    
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.showJsonLoader = false;
      return;
    }

    this.calcsFilingGroupStatusService.getFromFilingGroup(apiBase, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        this.FromFilingGroupList = (data.data && data.data.jsonObject) ? data.data.jsonObject : (data.jsonObject || []);
        if (this.FromFilingGroupList.length > 0) {
          this.selectedFromFilingGroup = this.FromFilingGroupList[0];
        }
        this.fromTaxYearFlag = false;
        this.showJsonLoader = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching from filing group:', error);
        this.showJsonLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading filing group data', 'gtw-snackbar--red');
      }
    );
  }

  fetchToFilingGroup(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.jcdKeyForRequest()
    };
    
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.showJsonLoader = false;
      return;
    }

    this.calcsFilingGroupStatusService.getFromFilingGroup(apiBase, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        this.ToFilingGroupList = (data.data && data.data.jsonObject) ? data.data.jsonObject : (data.jsonObject || []);
        if (this.ToFilingGroupList.length > 0) {
          this.selectedToFilingGroup = this.ToFilingGroupList[0];
        }
        this.toTaxYearFlag = false;
        this.showJsonLoader = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching to filing group:', error);
        this.showJsonLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading filing group data', 'gtw-snackbar--red');
      }
    );
  }

  fromTaxYearChange(taxYear: any): void {
    if (!taxYear) return;
    const wave = this.nextFetchWave();
    this.fromTaxYearFlag = true;
    this.fetchFromScenario(taxYear.value, taxYear.scenario, wave);
    this.fetchFromFilingGroup(taxYear.value, taxYear.scenario, wave);
  }

  toTaxYearChange(taxYear: any): void {
    if (!taxYear) return;
    const wave = this.nextFetchWave();
    this.toTaxYearFlag = true;
    this.fetchToScenario(taxYear.value, taxYear.scenario, wave);
    this.fetchToFilingGroup(taxYear.value, taxYear.scenario, wave);
  }

  copySelectedChanges(): void {
    if (this.copyInFlight) {
      return;
    }
    if (!this.selectedFromTaxYear || !this.selectedToTaxYear || 
        !this.selectedFromScenario || !this.selectedToScenario ||
        !this.selectedFromFilingGroup || !this.selectedToFilingGroup) {
      this.gtwSnackbarService.openSnackBar('Please select all required fields', 'gtw-snackbar--red');
      return;
    }

    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.gtwSnackbarService.openSnackBar('API base URL is not configured.', 'gtw-snackbar--red');
      return;
    }

    this.copyInFlight = true;
    this.showJsonLoader = true;
    const saveWave = this.nextSaveWave();

    const params: any = {
      from_tax_year: this.selectedFromTaxYear.value,
      from_scenario: this.selectedFromScenario.VALUE,
      from_fg: this.selectedFromFilingGroup.VALUE,
      to_tax_year: this.selectedToTaxYear.value,
      to_scenario: this.selectedToScenario.VALUE,
      to_fg: this.selectedToFilingGroup.VALUE,
      tax_year: this.globalParams?.tax_year || '',
      scenario: this.globalParams?.scenario || '',
      jcd_key: this.globalParams?.jcd_key ?? 250,
      screen_key: this.globalParams?.screen_key || '',
      jcd_ta_key: this.globalParams?.jcd_ta_key || '',
      activity_key: this.globalParams?.activity_key || ''
    };

    const tempObj = _.merge({}, this.globalParams || {}, params);
    const finalParams = _.merge({}, tempObj, params);

    this.calcsFilingGroupStatusService.saveCopyChanges(apiBase, finalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (saveWave !== this.saveWave) {
          return;
        }
        this.showJsonLoader = false;
        this.copyInFlight = false;
        const responseData = data.data || data;
        if (responseData.callSuccess == "1") {
          this.gtwSnackbarService.openSnackBar('Filing group data copied successfully.', 'gtw-snackbar--green');
          this.emitRequestGridUpdate('refresh');
        } else {
          const errorMessage = (responseData.errorMessage && responseData.errorMessage !== 'null' && responseData.errorMessage !== '') 
            ? responseData.errorMessage 
            : "Error while copying filing group data.";
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        if (saveWave !== this.saveWave) {
          return;
        }
        console.error('Error copying filing group data:', error);
        this.showJsonLoader = false;
        this.copyInFlight = false;
        this.gtwSnackbarService.openSnackBar('Error while copying filing group data.', 'gtw-snackbar--red');
      }
    );
  }
}
